import { ADMIN_API_ENDPOINTS } from "@/config/apiEndpoints/adminUrls";
import {
  ALLOWED_ADMIN_UPLOAD_MIME_TYPES,
  BLOCKED_UPLOAD_EXTENSIONS,
  formatFileSize,
  getFileExtension,
  isBlockedUploadExtension,
  MAX_BUCKET_UPLOAD_BYTES,
} from "@/constants/fileUpload";
import type { ToastProps } from "@/hooks/useToast";
import adminAxios from "@/lib/axios";

interface UploadResponse {
  url: string;
  fileName: string;
  fileType: string;
}

interface UploadOptions {
  /**
   * File Bucket only: accept any file except BLOCKED_UPLOAD_EXTENSIONS, up to
   * MAX_BUCKET_UPLOAD_BYTES — instead of the narrow image/PDF/video allow list
   * and the 200MB cap every other admin upload keeps. The server applies the
   * same limits and requires an admin session for this path.
   */
  allowAnyType?: boolean;
}

export async function uploadFile(
  file: File,
  toast: ({ ...props }: ToastProps) => void,
  options: UploadOptions = {}
): Promise<string | null> {
  if (options.allowAnyType) {
    if (file.size > MAX_BUCKET_UPLOAD_BYTES) {
      toast({
        variant: "destructive",
        title: "File Too Large",
        description: `File Bucket uploads must be ${formatFileSize(MAX_BUCKET_UPLOAD_BYTES)} or smaller (this file is ${formatFileSize(file.size)}).`,
      });
      return null;
    }

    if (isBlockedUploadExtension(file.name)) {
      toast({
        variant: "destructive",
        title: "Invalid File Type",
        description: `.${getFileExtension(file.name)} files cannot be uploaded. Blocked: ${BLOCKED_UPLOAD_EXTENSIONS.slice(0, 6).join(", ")} and other executable or markup files.`,
      });
      return null;
    }
  } else {
    // Validate file size (200MB limit)
    const maxSize = 200 * 1024 * 1024; // 200MB
    if (file.size > maxSize) {
      toast({
        variant: "destructive",
        title: "File Too Large",
        description: "File size should be less than 200MB",
      });
      return null;
    }

    if (!ALLOWED_ADMIN_UPLOAD_MIME_TYPES.includes(file.type)) {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      toast({
        variant: "destructive",
        title: "Invalid File Type",
        description: `Allowed: images (JPEG, PNG, GIF, WebP), PDF, or video (MP4, WebM, QuickTime). Got: ${fileExtension ? `.${fileExtension}` : file.type || "unknown"}`,
      });
      return null;
    }
  }

  try {
    const formData = new FormData();
    formData.append("file", file);
    if (options.allowAnyType) {
      formData.append("allowAnyType", "true");
    }

    const response = await adminAxios.post<UploadResponse>(
      ADMIN_API_ENDPOINTS.UPLOAD_FILE,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    // Backend wraps response in { success: true, data: { url, fileName, fileType } }
    const uploadData = (response.data as any)?.data || response.data;

    if (response.status === 200 && uploadData?.fileName) {
      // Frontend prepends CDN URL to filename
      const fullUrl = `${process.env["NEXT_PUBLIC_S3_CDN"]}/${uploadData.fileName}`;
      
      return fullUrl;
    }

    throw new Error("Upload failed");
  } catch (error: any) {
    console.error("File upload error:", error);

    toast({
      variant: "destructive",
      title: "Upload Failed",
      description: error.response?.data?.message || "Failed to upload file. Please try again.",
    });

    return null;
  }
}
